/**
 * @file defaults.ts
 * @description Умолчания секции v2 (phase19.16 Р2; db-design-r2 §«Payload
 * секции v2», uiux-design-r2 §2, §4): пустая секция в одну колонку и
 * элементы по виду — для кнопки «⊕ элемент» в кабинете, слияния недостающих
 * ключей при чтении (read.ts) и сборки заготовок (presets/).
 *
 * Умолчания нейтральны (SB27): без цветов, картинок и названий магазина;
 * тексты — общие заглушки, места под фото пустые (`mediaId: null`, SB21).
 *
 * Новое поле элемента добавляется сюда же — тогда старые данные без него
 * читаются с этим умолчанием без ошибок (так добавлены `rows` у «Сведений
 * о товаре», приёмка Р2а 2026-10-11).
 *
 * v3 (SB120 этап 1, db-design-sb120-stage1 §1): здесь нет ни одного
 * наследуемого ключа («как у сайта», «как на ПК», «Авто» = нет ключа) —
 * иначе `mergeMissingKeys` при чтении подставил бы число вместо
 * наследования. Обязательные поля — с нейтральными значениями: высота «по
 * содержимому», подложка выключена, наезда нет, доли [12].
 *
 * v4 (SB120 этап 3а, db-design-sb120-stage3 §4): у элементов нет «Цвета
 * текста» и «Шрифта» одним значением (их части — `typo`, нет ключа —
 * «Авто»), у «Надписи» стиль — `preset`, у плиток и категорий высота —
 * пропорция `aspect` 1 (прежняя «Обычная»). Прежнее `defaultTextStyle` —
 * в legacy-v3.ts (подъём v1 → v2).
 */

import {
  defaultProductFactRows,
  type SectionColumn,
  type SectionDecor,
  type SectionHeight,
  type SectionMediaBg,
  type SectionOverlay,
  type SectionElementKind,
  type SectionElementOf,
  type SectionLook,
  type SectionPayload,
} from "./schema.js";
import { deepFreeze } from "../site-theme/deep-freeze.js";

/**
 * Оформление по умолчанию: прозрачный фон, по сетке (нет `container`),
 * отступы и доля телефона «как у сайта» (нет ключей), без подложки, по
 * содержимому, видна везде.
 */
export function defaultSectionLook(): SectionLook {
  return {
    bg: { kind: "transparent" },
    card: { on: false },
    height: { kind: "content" },
    decor: [],
    space: { overlap: 0, mergeWithPrev: false },
    visibility: { phone: true, tablet: true, desktop: true },
  };
}

/**
 * Высота нового блока с фото фоном (вопрос 7, решение пользователя
 * 2026-10-18): на ПК «По фото» (фото целиком), на телефоне «По кадру
 * телефона 4:5». Кабинет ставит её при выборе фото фоном у блока «по
 * содержимому»; старые блоки не трогаются.
 */
export function photoBackgroundHeight(): { height: SectionHeight; heightPhone: SectionHeight } {
  return { height: { kind: "photo" }, heightPhone: { kind: "photo", aspect: 0.8 } };
}

/** Новый фон-фото: фокус по центру, без растворения и затемнения, светлый набор текста, без выхода за край. */
export function defaultMediaBackground(mediaId: string): SectionMediaBg {
  return {
    kind: "media",
    mediaId,
    focus: { x: 50, y: 50 },
    fade: "none",
    dim: 0,
    textTone: "light",
    bleed: { top: 0, bottom: 0, side: "none" },
  };
}

/** Новый рисунок: по центру, M (160 px, на телефоне — × доля темы), скрыт на телефоне, к секции. */
export function defaultSectionDecor(mediaId: string): SectionDecor {
  return { mediaId, x: 50, y: 50, size: 160, hideOnPhone: true, bleed: false, column: null };
}

/**
 * Новая надпись на фото (v4): капсом, по центру кадра, плашка «как было»
 * (нет `plate` — чёрная 60 %), видна на телефоне.
 */
export function defaultSectionOverlay(text: string): SectionOverlay {
  return { text, preset: "caps", x: 50, y: 50, hideOnPhone: false };
}

/** Пустая колонка. */
export function emptySectionColumn(): SectionColumn {
  return { elements: [] };
}

/** Новая пустая секция: одна колонка, без шапки. */
export function defaultSectionPayload(): SectionPayload {
  return {
    columns: {
      shares: [12],
      // uiux-design-r2 §2: по вертикали — «Центр» по умолчанию. Расстояния — «как у сайта» (нет ключей).
      valign: "center",
      items: [emptySectionColumn()],
    },
    look: defaultSectionLook(),
  };
}

/** Замороженное умолчание (для сравнения и слияния; наружу — копии через `defaultSectionPayload`). */
export const DEFAULT_SECTION_PAYLOAD: Readonly<SectionPayload> = deepFreeze(defaultSectionPayload());

/** Поля элемента без общих (`id`, `hide`, `space`, `align`) — то, что задаёт вид. */
type ElementBody<K extends SectionElementKind> = Omit<SectionElementOf<K>, "id" | "hide" | "space" | "align">;

/**
 * Содержимое нового элемента по виду (без id). Реестр — OCP: новый вид
 * элемента = новая запись; TypeScript требует запись для каждого вида.
 */
const ELEMENT_BODIES: { readonly [K in SectionElementKind]: () => ElementBody<K> } = {
  heading: () => ({ kind: "heading", level: 2, text: "Заголовок" }),
  eyebrow: () => ({ kind: "eyebrow", text: "Надпись", preset: "caps" }),
  text: () => ({ kind: "text", text: "Короткий текст о том, чем вы занимаетесь." }),
  // «По фото» — как прежнее «Обычное» (решение оркестратора 2026-10-18 (б)).
  image: () => ({ kind: "image", mediaId: null, mode: "fit", height: { kind: "photo" }, overlays: [] }),
  gallery: () => ({ kind: "gallery", mediaIds: [], perRow: 3, phoneLayout: "scroll" }),
  // У видео из медиатеки файл обязателен: кабинет создаёт элемент после выбора файла.
  video: () => ({ kind: "video", mediaId: "", posterMediaId: null }),
  audio: () => ({ kind: "audio", mediaId: "" }),
  button: () => ({
    kind: "button",
    label: "Подробнее",
    target: { type: "system", route: "CATALOG" },
    variant: "primary",
    iconPosition: "right",
    phoneFull: false,
    newTab: false,
    inline: false,
  }),
  tiles: () => ({
    kind: "tiles",
    cols: 3,
    look: "overlay",
    tone: "light",
    aspect: 1,
    phoneLayout: "stack",
    items: [1, 2, 3].map(() => ({ mediaId: null, title: "Название", target: null })),
  }),
  products: () => ({
    kind: "products",
    source: "new",
    badge: null,
    ids: [],
    limit: 4,
    perRow: 4,
    phoneLayout: "scroll",
  }),
  stats: () => ({
    kind: "stats",
    items: [
      { id: "s1", value: "10+", label: "Короткая подпись" },
      { id: "s2", value: "500+", label: "Короткая подпись" },
      { id: "s3", value: "24/7", label: "Короткая подпись" },
    ],
    perRow: 3,
    size: "l",
  }),
  table: () => ({
    kind: "table",
    columns: [
      { id: "c1", title: "Столбец 1" },
      { id: "c2", title: "Столбец 2" },
    ],
    rows: [{ id: "r1", cells: ["", ""] }],
    firstColumnBold: true,
    phoneLayout: "stack",
  }),
  categories: () => ({
    kind: "categories",
    source: "auto",
    ids: [],
    limit: 3,
    cols: 3,
    look: "overlay",
    aspect: 1,
    phoneLayout: "stack",
  }),
  articles: () => ({
    kind: "articles",
    source: "latest",
    ids: [],
    limit: 3,
    show: { category: true, author: false, date: true },
  }),
  reviews: () => ({ kind: "reviews", source: "product", pick: "best", ids: [], limit: 6, layout: "carousel" }),
  faq: () => ({
    kind: "faq",
    items: [{ q: "Вопрос покупателя", a: "Короткий понятный ответ." }],
    firstOpen: true,
  }),
  contacts: () => ({ kind: "contacts", kinds: ["phone", "email", "address"] }),
  newsletter: () => ({ kind: "newsletter" }),
  // Один пустой пункт: кабинет сразу ставит курсор в текст (без текста не сохранится).
  announcements: () => ({
    kind: "announcements",
    source: "manual",
    items: [{ id: "a1", text: "" }],
    limit: 5,
    promoTextMode: "auto",
    layout: "strip",
    intervalSec: 6,
    perRow: 3,
    phoneLayout: "scroll",
  }),
  // Р2а: элементы данных товара. Умолчания = витрина до Р2а (сверено с HEAD
  // apps/web product/[slug]/page.tsx 2026-10-12): миниатюры под фото без
  // увеличения, H1 с рейтингом, фасовки кнопками, счётчик количества, кнопка
  // по тексту, 3 отзыва, 4 похожих (на телефоне — по одному).
  breadcrumbs: () => ({ kind: "breadcrumbs" }),
  "product-gallery": () => ({ kind: "product-gallery", layout: "thumbs-below", zoom: false }),
  "product-title": () => ({ kind: "product-title", showRating: true }),
  "product-buy": () => ({ kind: "product-buy", skuPicker: "chips", showQuantity: true, buttonWidth: "auto" }),
  "product-badges": () => ({ kind: "product-badges" }),
  "product-short": () => ({ kind: "product-short" }),
  "product-description": () => ({ kind: "product-description", collapsible: false }),
  // Строки сведений — все включены (решение 2026-10-11, SB35); старый элемент
  // без `rows` при чтении получает это же умолчание.
  "product-facts": () => ({ kind: "product-facts", look: "rows", rows: defaultProductFactRows() }),
  "product-specs": () => ({ kind: "product-specs", look: "table" }),
  "product-tags": () => ({ kind: "product-tags", look: "cloud" }),
  "product-reviews": () => ({ kind: "product-reviews", limit: 3 }),
  "product-related": () => ({ kind: "product-related", limit: 4, perRow: 4, phoneLayout: "stack" }),
  "product-seller": () => ({ kind: "product-seller", look: "compact" }),
};

/** Содержимое элемента по виду без id — умолчания для слияния при чтении. */
export function defaultElementBody<K extends SectionElementKind>(kind: K): ElementBody<K> {
  return ELEMENT_BODIES[kind]();
}

/**
 * Новый элемент для кнопки «⊕ элемент». `id` — уникальный в блоке; если не
 * задан, берётся `el-<вид>` (кабинет передаёт свой, сервер проверяет уникальность).
 * Видео и аудио из медиатеки создаются с пустым `mediaId` — кабинет сразу
 * открывает медиатеку, без выбора файла элемент не сохранится.
 */
export function defaultElement<K extends SectionElementKind>(kind: K, id?: string): SectionElementOf<K> {
  return { id: id ?? `el-${kind}`, ...ELEMENT_BODIES[kind]() } as SectionElementOf<K>;
}
